import React, { useState } from "react";
import {
  Form,
  Space,
  Modal,
  Upload,
  Button,
  Table,
  App,
  Typography,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../../service/apiRequest";

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const sampleTemplate = [
  {
    emp_code: "SVICON0001",
    emp_first_name: "Rahul",
    emp_last_name: "Sharma",
    emp_department_name: "Machinery",
    emp_employee_type: "Full Time",
    emp_salary: 25000,
    emp_address: "123 Main St, City",
  },
  {
    emp_code: "SVICON0002",
    emp_first_name: "Amit",
    emp_last_name: "Verma",
    emp_department_name: "Assembly",
    emp_employee_type: "Part Time",
    emp_salary: 18000,
    emp_address: "456 Park Ave, City",
  },
];

const ImportEmployeeModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(sampleTemplate);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EmployeeTemplate");
    XLSX.writeFile(wb, "employee_sample_template.xlsx");
  };

  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

      if (!json.length) {
        message.warning("The Excel file is empty.");
        setParsedData([]);
        return false;
      }

      setParsedData(json);
      message.success("File loaded successfully!");
    } catch (err) {
      console.error(err);
      message.error("Failed to read Excel file. Please check the format.");
      setParsedData([]);
    } finally {
      setLoading(false);
    }

    return false; // prevent auto upload
  };

  const handleImport = async () => {
    if (!parsedData.length) return;
    setLoading(true);
    try {
      const res = await apiRequest<any>({
        method: "post",
        url: "/import-employees",
        data: parsedData,
      });

      message.success(res?.message || "Employees imported successfully!");
      setParsedData([]);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Import failed. Please check the data format."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setParsedData([]);
    onClose();
  };

  return (
    <Modal
      title="Import Employees"
      width="80%"
      open={visible}
      onCancel={handleCancel}
      onOk={handleImport}
      okText="Import"
      okButtonProps={{ disabled: parsedData.length === 0 || loading, loading }}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Step 1: Download Sample Excel Template">
          <Space direction="vertical" size={4}>
            <Text type="secondary">
              Download sample template with standard columns (emp_first_name, emp_last_name, emp_department_name, emp_employee_type, emp_salary, emp_address)
            </Text>
            <Button icon={<DownloadOutlined />} onClick={downloadSample}>
              Download Sample Template
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Step 2: Upload Filled Excel File">
          <Space direction="vertical" size={4}>
            <Text type="secondary">Select your completed Excel file (.xlsx)</Text>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Excel File
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>

      {parsedData.length > 0 && (
        <>
          <Text style={{ marginTop: 12, display: "block", fontWeight: 600 }}>
            Preview of Data to Import ({parsedData.length} items):
          </Text>
          <Table
            dataSource={parsedData}
            rowKey={(_, index) => index!.toString()}
            size="small"
            scroll={{ y: 260 }}
            pagination={false}
            columns={[
              {
                title: "Emp Code (emp_code)",
                dataIndex: "emp_code",
                key: "emp_code",
                render: (v, r) => v || r["employee_code"] || r["Emp Code"] || "(Auto Generated)",
              },
              {
                title: "First Name (emp_first_name)",
                dataIndex: "emp_first_name",
                key: "emp_first_name",
                render: (v, r) => v || r["employee_fname"] || r["First Name"] || "-",
              },
              {
                title: "Last Name (emp_last_name)",
                dataIndex: "emp_last_name",
                key: "emp_last_name",
                render: (v, r) => v || r["employee_lname"] || r["Last Name"] || "-",
              },
              {
                title: "Department (emp_department_name)",
                dataIndex: "emp_department_name",
                key: "emp_department_name",
                render: (v, r) => v || r["department_name"] || r["Department"] || "-",
              },
              {
                title: "Employee Type (emp_employee_type)",
                dataIndex: "emp_employee_type",
                key: "emp_employee_type",
                render: (v, r) => v || r["employee_type"] || r["Employee Type"] || "-",
              },
              {
                title: "Salary (emp_salary)",
                dataIndex: "emp_salary",
                key: "emp_salary",
                render: (v, r) => v ?? r["employee_salary"] ?? 0,
              },
              {
                title: "Address (emp_address)",
                dataIndex: "emp_address",
                key: "emp_address",
                render: (v, r) => v || r["employee_address"] || r["Address"] || "-",
              },
            ]}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportEmployeeModal;
